import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Palette, Calendar, FileText, TrendingUp,
  Mail, Image as ImageIcon, Activity, Newspaper, AlertTriangle, X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import BuyFlowWidget from './BuyFlowWidget';
import PaidOrders from './PaidOrders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    artworks: 0,
    artists: 0,
    events: 0,
    contacts: 0,
    media: 0,
    pages: 0,
    subscribers: 0,
    consignments: 0,
    consultations: 0,
  });
  const [missingImages, setMissingImages] = useState({ count: 0, items: [] });
  const [showMissingModal, setShowMissingModal] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchMissingImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMissingImages = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/artworks/missing-images`, { withCredentials: true });
      setMissingImages(r.data || { count: 0, items: [] });
    } catch (e) { /* dashboard tile is best-effort */ }
  };

  const fetchStats = async () => {
    // 1) Try the consolidated endpoint first — one round-trip, never zeroes out
    //    everything if one collection is unhappy.
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        withCredentials: true,
      });
      setStats((s) => ({ ...s, ...r.data }));
      return;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[dashboard] /api/admin/stats unavailable, falling back', err?.response?.status);
      }
    }

    // 2) Fallback: parallel public/admin counts with allSettled so one
    //    failure doesn't take down the whole dashboard.
    const calls = [
      ['artworks',  axios.get(`${BACKEND_URL}/api/artworks?include_hidden=1`)],
      ['artists',   axios.get(`${BACKEND_URL}/api/artists?include_hidden=1`)],
      ['events',    axios.get(`${BACKEND_URL}/api/events`)],
      ['contacts',  axios.get(`${BACKEND_URL}/api/admin/contacts`, { withCredentials: true })],
      ['media',     axios.get(`${BACKEND_URL}/api/media`)],
    ];
    const results = await Promise.allSettled(calls.map(([, p]) => p));
    const next = {};
    results.forEach((res, i) => {
      const key = calls[i][0];
      if (res.status !== 'fulfilled') return;
      const data = res.value.data;
      if (key === 'contacts') next[key] = data?.contacts?.length ?? 0;
      else if (key === 'media') next[key] = data?.count ?? data?.media?.length ?? 0;
      else next[key] = Array.isArray(data) ? data.length : (data?.length ?? 0);
    });
    setStats((s) => ({ ...s, ...next }));
  };

  const statCards = [
    { icon: Palette, label: 'Artworks', value: stats.artworks, color: 'bg-blue-500' },
    { icon: Users, label: 'Artists', value: stats.artists, color: 'bg-purple-500' },
    { icon: Calendar, label: 'Events', value: stats.events, color: 'bg-green-500' },
    { icon: Mail, label: 'New Contacts', value: stats.contacts, color: 'bg-red-500' },
    { icon: ImageIcon, label: 'Media Files', value: stats.media, color: 'bg-yellow-500' },
  ];

  return (
    <div className="p-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-light mb-2">Welcome Back</h1>
        <p className="text-gray-600">Zebra One Gallery Admin Dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center text-white`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
              <p className="text-3xl font-bold mb-1">{stat.value}</p>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Artworks Missing Images — flag row. Amber pill when any exist. */}
      {missingImages.count > 0 && (
        <motion.button
          type="button"
          onClick={() => setShowMissingModal(true)}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full mb-8 flex items-center gap-3 p-4 bg-amber-50 border border-amber-300 rounded-xl hover:bg-amber-100 text-left transition-colors"
          data-testid="missing-images-tile"
        >
          <div className="bg-amber-500 w-10 h-10 rounded-lg flex items-center justify-center text-white">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              {missingImages.count} artwork{missingImages.count === 1 ? '' : 's'} missing images
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              These show "Image Coming Soon" to visitors. Click to review & edit.
            </p>
          </div>
          <span className="text-xs uppercase tracking-[3px] text-amber-800">Review</span>
        </motion.button>
      )}

      {/* Missing-images modal */}
      {showMissingModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowMissingModal(false)}
          data-testid="missing-images-modal-backdrop"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="missing-images-modal"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-light">Artworks missing images</h3>
                <p className="text-xs text-gray-500 mt-0.5">{missingImages.count} to review · newest first</p>
              </div>
              <button
                type="button"
                onClick={() => setShowMissingModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
                data-testid="missing-images-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {missingImages.items.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded hover:border-black transition-colors"
                >
                  <div className="w-12 h-12 bg-amber-50 flex items-center justify-center rounded shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{row.title}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {row.artistName}
                      {row.hidden ? ' · hidden' : ''}
                      {row.available === false ? ' · unavailable' : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowMissingModal(false); navigate(`/admin/artworks?edit=${row.id}`); }}
                    className="px-3 py-1.5 text-xs bg-black text-white rounded hover:bg-gray-800"
                    data-testid={`missing-images-edit-${row.id}`}
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Buy-flow conversion stats — full-width above the activity/quick-action row */}
        <BuyFlowWidget />
        <PaidOrders />

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Activity className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Recent Activity</h2>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((item) => (
              <div key={item} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">New artwork added</p>
                  <p className="text-xs text-gray-500">2 hours ago</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/admin/artworks?new=1')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-black transition-colors text-left"
              data-testid="quick-action-add-artwork"
            >
              <Palette className="w-5 h-5 mb-2" />
              <p className="font-medium text-sm">Add Artwork</p>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/admin/artists?new=1')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-black transition-colors text-left"
              data-testid="quick-action-add-artist"
            >
              <Users className="w-5 h-5 mb-2" />
              <p className="font-medium text-sm">Add Artist</p>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/admin/media')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-black transition-colors text-left"
              data-testid="quick-action-upload-media"
            >
              <ImageIcon className="w-5 h-5 mb-2" />
              <p className="font-medium text-sm">Upload Media</p>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/admin/pages')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-black transition-colors text-left"
              data-testid="quick-action-edit-pages"
            >
              <FileText className="w-5 h-5 mb-2" />
              <p className="font-medium text-sm">Edit Pages</p>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/admin/news?new=1')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-black transition-colors text-left"
              data-testid="quick-action-write-post"
            >
              <Newspaper className="w-5 h-5 mb-2" />
              <p className="font-medium text-sm">Write Post</p>
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;